import React from 'react';
import { GoClockFill } from 'react-icons/go';
import { BsFillCalendarDateFill, BsPersonWalking } from 'react-icons/bs';
import { PiUsersFourFill } from 'react-icons/pi';
import { SiWikibooks } from 'react-icons/si';
import { MdExpandCircleDown } from 'react-icons/md';
import { FaClipboardList } from 'react-icons/fa';
import { IoReloadCircle } from 'react-icons/io5';

interface Session {
  id: string;
  type: 'solo' | 'team';
  goal: string;
  duration: number;
  completedAt: string;
  notes: string;
  members: string[];
  xp: number;
  music?: string;
  tasks?: any[];
}

interface RecentSessionsProps {
  sessions: Session[];
  expandedSessions: Record<string, boolean>;
  setExpandedSessions: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onViewAll: () => void;
  onViewDetails: (session: Session) => void;
  onRepeatSession: (session: Session) => void;
}

// Helper function to format duration
// Helper function to format duration
const formatDuration = (durationInMinutes: number): string => {
  const mins = Math.floor(durationInMinutes);
  const secs = Math.round((durationInMinutes - mins) * 60);

  if (mins === 0 && secs === 0) return '0s';
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
};

const RecentSessions: React.FC<RecentSessionsProps> = ({
  sessions,
  expandedSessions,
  setExpandedSessions,
  onViewAll,
  onViewDetails,
  onRepeatSession,
}) => {
  return (
    <div className="mt-12 sm:mt-16">
      <div className="text-center mb-10 sm:mb-12">
        <h3 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-2">
          Recent Sessions
        </h3>
        <p className="text-base text-gray-600 font-light">Your focus history</p>
      </div>

      <div className="max-w-3xl mx-auto">
        <div className="grid gap-4 sm:gap-5">
          {sessions.map((session) => (
            <div
              key={session.id}
              className="bg-white border border-gray-200/60 rounded-2xl p-5 sm:p-6 hover:border-orange-200/80 hover:shadow-sm transition-all duration-300 cursor-pointer group"
              onClick={() => setExpandedSessions(prev => ({
                ...prev,
                [session.id]: !prev[session.id]
              }))}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Session Type Tag */}
                  <div className={`px-3.5 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 flex-shrink-0 ${session.type === 'solo'
                      ? 'bg-orange-50 text-orange-600 border border-orange-200/50'
                      : 'bg-orange-50 text-orange-600 border border-orange-200/50'
                    }`}>
                    {session.type === 'solo' ? (
                      <>
                        <BsPersonWalking className="text-sm" />
                        <span>Solo</span>
                      </>
                    ) : (
                      <>
                        <PiUsersFourFill className="text-sm" />
                        <span>Team</span>
                      </>
                    )}
                  </div>

                  {/* Session Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2 truncate group-hover:text-orange-600/80 transition-colors">
                      {session.goal}
                    </h4>
                    <div className="flex items-center gap-5 text-sm text-gray-500">
                      <span className="flex items-center gap-2">
                        <GoClockFill className="text-orange-500/70" />
                        <span className="font-normal">{formatDuration(session.duration)}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <BsFillCalendarDateFill className="text-orange-500/70" />
                        <span className="font-normal">{new Date(session.completedAt).toLocaleDateString()}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expand/Collapse Icon */}
                <div className="text-gray-400 flex-shrink-0">
                  <MdExpandCircleDown
                    className={`w-5 h-5 transition-transform duration-300 ${expandedSessions[session.id] ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>

              {/* Expanded Content */}
              {expandedSessions[session.id] && (
                <div className="mt-5 pt-5 border-t border-gray-200/40">
                  <div className="space-y-4">



                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails(session);
                        }}
                        className="px-4 py-2 bg-white border border-orange-200/50 text-orange-600 rounded-lg text-sm font-normal hover:bg-orange-50/50 transition-colors flex items-center gap-2"
                      >
                        <FaClipboardList /> View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRepeatSession(session);
                        }}
                        className="px-4 py-2 bg-white border border-orange-200/50 text-orange-600 rounded-lg text-sm font-normal hover:bg-orange-50/50 transition-colors flex items-center gap-2"
                      >
                        <IoReloadCircle /> Repeat Session <span className="text-orange-500/70">({formatDuration(session.duration)})</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* View All Button */}
      <div className="text-center mt-10 sm:mt-12">
        <button
          onClick={onViewAll}
          className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-orange-200/60 hover:border-orange-300/80 text-orange-600 hover:text-orange-700 rounded-full text-sm font-medium transition-all duration-300 hover:bg-orange-50/30"
        >
          <SiWikibooks className="text-base" />
          <span>View All Sessions</span>
        </button>
      </div>
    </div>
  );
};

export default RecentSessions;
